import React, { useState, useEffect } from 'react';
import './TopWidgetEdit.css';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import LayoutSelector2025 from '../../components/LayoutSelector2025';
import TextSettingsSection from '../WidgetEditComponents/TextSettingsSection';
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import AdvancedSettingsSection from '../WidgetEditComponents/AdvancedSettingsSection';
import IconPicker from '../IconPicker';
import ServicesManagement from '../WidgetEditComponents/ServicesManagement';
import ImageTooBigPopup from '../WidgetEditComponents/ImageTooBigPopup';
import RevolutionaryLayoutSettings from '../WidgetEditComponents/RevolutionaryLayoutSettings';
import SaveTemplatePopup from '../WidgetEditComponents/SaveTemplatePopup';

function SimplisticServicesEdit({ widget, onUpdate, colors }) {
    const { editData, setEditData, handleInputChange, uploadImage, handleSliderChange } = useWidgetEdit(widget, onUpdate);
    
    console.log('🚀 SimplisticServicesEdit loaded:', {
        widgetType: widget?.type,
        widgetId: widget?.id,
        layoutType: editData?.layoutType,
        serviceLayout: editData?.serviceLayout
    });
    
    // ✅ RACE CONDITION PREVENTION - KRITISCH für Widget-Switches
    const [isInitialized, setIsInitialized] = useState(false);
    const [previousLayoutType, setPreviousLayoutType] = useState(editData.layoutType);
    
    // ✅ SAVE TEMPLATE POPUP STATE
    const [showSaveTemplatePopup, setShowSaveTemplatePopup] = useState(false);
    
    // ✅ INITIALIZATION EFFECT - Set initialized flag after first render AND ensure template defaults
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsInitialized(true);
            console.log('🚀 SimplisticServices initialized - layout variations now active');
            
            // ✅ CRITICAL FIX: Ensure template defaults are set on initialization
            if (editData.showServiceIcons === undefined) {
                console.log('🔧 Setting template default for showServiceIcons');
                const templateDefaultEvent = {
                    target: { 
                        name: 'showServiceIcons', 
                        value: true, 
                        type: 'checkbox',
                        checked: true
                    }
                };
                handleInputChange(templateDefaultEvent);
            }
            
            // ✅ FIX: Template hat invertIconColors: true, aber wir wollen false als Default
            if (editData.invertIconColors === true) {
                console.log('🔧 Fixing template default: invertIconColors should be false');
                const invertFixEvent = {
                    target: { 
                        name: 'invertIconColors', 
                        value: false, 
                        type: 'checkbox',
                        checked: false
                    }
                };
                handleInputChange(invertFixEvent);
            }
            
            // ✅ FIX: serviceIconBorderWidth sollte 1px sein für schwarzen Border (nur wenn undefined)
            if (editData.serviceIconBorderWidth === undefined) {
                console.log('🔧 Setting serviceIconBorderWidth to 1px for black border (template default)');
                const borderWidthEvent = {
                    target: { 
                        name: 'serviceIconBorderWidth', 
                        value: 1, 
                        type: 'range'
                    }
                };
                handleInputChange(borderWidthEvent);
            }
            
            // ✅ FIX: serviceMinHeight sollte 330px sein für konsistente Card-Höhen
            if (editData.serviceMinHeight === 280 || editData.serviceMinHeight === undefined) {
                console.log('🔧 Setting serviceMinHeight to 330px for consistent card heights');
                const minHeightEvent = {
                    target: { 
                        name: 'serviceMinHeight', 
                        value: 330, 
                        type: 'range'
                    }
                };
                handleInputChange(minHeightEvent);
            }
            
            // ✅ FIX: serviceSpacing sollte 24px sein (außer bei bordered-minimal = 0px)
            if ((editData.serviceSpacing === 0 || editData.serviceSpacing === undefined) && 
                editData.layoutType !== 'bordered-minimal') {
                console.log('🔧 Setting serviceSpacing to 24px for proper card spacing (not bordered-minimal)');
                const spacingEvent = {
                    target: { 
                        name: 'serviceSpacing', 
                        value: 24, 
                        type: 'range'
                    }
                };
                handleInputChange(spacingEvent);
            }
            
            // ✅ FIX: serviceIconBorderRadius sollte 100px sein für classic-grid (runde Icons)
            if ((editData.serviceIconBorderRadius === 12 || editData.serviceIconBorderRadius === undefined) && 
                (editData.layoutType === 'classic-grid' || editData.layoutType === undefined)) {
                console.log('🔧 Setting serviceIconBorderRadius to 100px for round icons (classic-grid)');
                const iconRadiusEvent = {
                    target: { 
                        name: 'serviceIconBorderRadius', 
                        value: 100, 
                        type: 'range'
                    }
                };
                handleInputChange(iconRadiusEvent);
            }
        }, 100); // Small delay to ensure editData is stable
        
        return () => clearTimeout(timer);
    }, [editData.showServiceIcons, editData.invertIconColors, editData.serviceMinHeight, editData.serviceSpacing, editData.serviceIconBorderRadius, editData.layoutType, handleInputChange]);

    // ✅ LAYOUT VARIATIONS SYSTEM mit Race Condition Prevention
    useEffect(() => {
        const currentLayout = editData.layoutType;
        
        // ✅ CRITICAL: Only apply layout variations on ACTUAL layout changes, not widget switches
        // ✅ EXCEPTION: Allow template defaults to be set even if not initialized yet
        const isTemplateDefaultMissing = editData.showServiceIcons === undefined;
        
        if (!isInitialized && !isTemplateDefaultMissing) {
            console.log('🚫 SimplisticServices: Skipping layout variations - not initialized yet');
            return;
        }
        
        if (!isInitialized && isTemplateDefaultMissing) {
            console.log('🔧 SimplisticServices: Allowing layout variations for template defaults');
        }
        
        if (previousLayoutType === currentLayout) {
            console.log('🚫 SimplisticServices: Skipping layout variations - same layout type');
            return;
        }
        
        console.log(`🚀 SimplisticServices REAL layout change detected: ${previousLayoutType} → ${currentLayout}`);
        setPreviousLayoutType(currentLayout);
        
        // 🎯 LAYOUT-SPEZIFISCHE DEFAULTS
        const layoutVariations = {
            'classic-grid': {
                // Standard Grid Layout mit Abständen
                height: 'auto',
                heightMobile: 'auto',
                minHeight: 400,
                minHeightMobile: 500,
                paddingTop: 60,
                paddingTopMobile: 40,
                serviceBackgroundColor: 'transparent',
                paddingBottom: 60,
                paddingBottomMobile: 40,
                servicesPerRow: 2,
                servicesPerRowMobile: 1,
                serviceLayout: 'classic-grid',
                servicePadding: 32,
                serviceSpacing: 24,
                serviceBorderRadius: 16,
                serviceShadow: true,
                // ✅ SERVICE CARD HEIGHT: 330px für Desktop und Mobile
                serviceMinHeight: 330,
                // ✅ CONTENT SPACING: Verhindert Rand-Kleben
                contentSpacing: 40,
                contentSpacingMobile: 20,
                // ✅ IMAGE SPACING: Standard Abstand für classic-grid
                serviceImageTopMargin: 0, // ✅ KEIN TOP MARGIN für classic-grid
                serviceImageTopMarginMobile: 0, // ✅ KEIN TOP MARGIN Mobile
                // ✅ ICON SETTINGS: Normale Icons (nicht invertiert) + runder Container
                invertIconColors: false,
                serviceIconBorderRadius: 100, // Runder Icon Container (100px)
                serviceIconBorderWidth: 1, // ✅ STANDARD BORDER WIDTH
                // ✅ IMAGE SIZE SETTINGS: Standard Image-Größen
                serviceImageWidth: 80,
                serviceImageHeight: 80,
                serviceImageWidthMobile: 70,
                serviceImageHeightMobile: 70,
                // ✅ BUTTON SETTINGS: Buttons deaktiviert für classic-grid
                showServiceButtons: false, // ✅ KEINE BUTTONS für classic-grid Layout
                // ✅ SERVICE VISIBILITY: Alle 4 Services sichtbar für classic-grid
                visibleServices: 4, // ✅ ALLE 4 SERVICES SICHTBAR (im Gegensatz zu three-column-clean)
                // ✅ TEXT VISIBILITY: Texte wieder einblenden (falls vorher bei clean ausgeblendet)
                showTitleText: true,
                showSubtitleText: true,
                containerBorder: true // Container-Borders wieder aktivieren
                // showServiceIcons wird nicht überschrieben - Template-Wert bleibt
            },
            'minimal-cards': {
                // Minimale Karten mit kantigem Border und ohne Icons
                height: 'auto',
                heightMobile: 'auto',
                minHeight: 400,
                minHeightMobile: 500,
                paddingTop: 60,
                paddingTopMobile: 40,
                paddingBottom: 60,
                serviceBackgroundColor: 'transparent',
                paddingBottomMobile: 40,
                servicesPerRow: 2,
                servicesPerRowMobile: 1,
                serviceLayout: 'minimal-cards',
                servicePadding: 32,
                serviceSpacing: 24,
                serviceBorderRadius: 0, // ✅ KANTIGE BORDERS (0px)
                serviceShadow: false, // ✅ KEIN SCHATTEN für minimales Design
                serviceBorder: true, // ✅ BORDER AKTIVIERT
                serviceBorderWidth: 1,
                serviceBorderColor: '#e5e5e5',
                // ✅ SERVICE CARD HEIGHT: 330px für Desktop und Mobile
                serviceMinHeight: 330,
                // ✅ CONTENT SPACING: Verhindert Rand-Kleben
                contentSpacing: 40,
                contentSpacingMobile: 20,
                // ✅ IMAGE SPACING: Standard Abstand für minimal-cards
                serviceImageTopMargin: 0, // ✅ KEIN TOP MARGIN für minimal-cards
                serviceImageTopMarginMobile: 0, // ✅ KEIN TOP MARGIN Mobile
                // ✅ BUTTON SETTINGS: Minimal Design mit eckigen Buttons
                serviceButtonBorderRadius: 0, // ✅ ECKIGE BUTTONS
                serviceButtonBorder: true, // ✅ BUTTON BORDER für minimales Design
                serviceButtonBorderWidth: 1, // ✅ DÜNNER BORDER
                serviceButtonShadow: false, // ✅ KEIN SCHATTEN für minimal design
                // ✅ ICON SETTINGS: Icons deaktiviert für minimales Design
                showServiceIcons: false, // ✅ KEINE ICONS
                invertIconColors: false,
                serviceIconBorderWidth: 1, // ✅ STANDARD BORDER WIDTH
                // ✅ IMAGE SIZE SETTINGS: Kleinere Images für minimal design
                serviceImageWidth: 60,
                serviceImageHeight: 60,
                serviceImageWidthMobile: 50,
                serviceImageHeightMobile: 50,
                // ✅ BUTTON SETTINGS: Buttons deaktiviert für minimal-cards
                showServiceButtons: false, // ✅ KEINE BUTTONS für minimal-cards Layout
                // ✅ SERVICE VISIBILITY: Alle 4 Services sichtbar für minimal-cards
                visibleServices: 4, // ✅ ALLE 4 SERVICES SICHTBAR
                // ✅ TEXT VISIBILITY: Texte wieder einblenden (falls vorher bei clean ausgeblendet)
                showTitleText: true,
                showSubtitleText: true,
                containerBorder: true // Container-Borders wieder aktivieren
            },
            'luxury-cards': {
                // Luxuriöse Karten mit mehr Spacing
                height: 'auto',
                heightMobile: 'auto',
                minHeight: 600,
                minHeightMobile: 700,
                paddingTop: 100,
                paddingTopMobile: 50,
                paddingBottom: 100,
                paddingBottomMobile: 50,
                servicesPerRow: 2,
                servicesPerRowMobile: 1,
                serviceLayout: 'luxury-cards',
                servicePadding: 40,
                serviceSpacing: 32,
                serviceBorderRadius: 20,
                serviceShadow: true,
                // ✅ SERVICE CARD HEIGHT: 330px für Desktop und Mobile
                serviceMinHeight: 330,
                // ✅ ICON SETTINGS: Normale Icons (nicht invertiert)
                invertIconColors: false,
                serviceIconBorderWidth: 1, // ✅ STANDARD BORDER WIDTH
                // ✅ IMAGE SIZE SETTINGS: Größere Images für luxury design
                serviceImageWidth: 100,
                serviceImageHeight: 100,
                serviceImageWidthMobile: 80,
                serviceImageHeightMobile: 80,
                serviceBackgroundColor: 'transparent',
                // ✅ BUTTON SETTINGS: Buttons deaktiviert für luxury-cards
                showServiceButtons: false, // ✅ KEINE BUTTONS für luxury-cards Layout
                // ✅ SERVICE VISIBILITY: Alle 4 Services sichtbar für luxury-cards
                visibleServices: 4, // ✅ ALLE 4 SERVICES SICHTBAR
                // ✅ TEXT VISIBILITY: Texte wieder einblenden (falls vorher bei clean ausgeblendet)
                showTitleText: true,
                showSubtitleText: true,
                containerBorder: true // Container-Borders wieder aktivieren
                // showServiceIcons wird nicht überschrieben - Template-Wert bleibt
            },
            'bordered-minimal': {
                // Minimalistisches Layout mit Borders wie im Bild
                height: 'auto',
                heightMobile: 'auto',
                minHeight: 0,
                minHeightMobile: 0,
                paddingTop: 0,
                paddingTopMobile: 0,
                paddingBottom: 0,
                paddingBottomMobile: 0,
                servicesPerRow: 4,
                servicesPerRowMobile: 1,
                serviceLayout: 'bordered-minimal',
                servicePadding: 40,
                servicePaddingHorizontal: 25,
                serviceSpacing: 0, // Kein Gap - nahtlose Borders
                serviceBorderRadius: 0,
                serviceShadow: false,
                serviceBorder: true,
                serviceBorderColor: '#e5e5e5',
                serviceBackgroundColor: 'transparent', // Kein Background
                // Icon Settings für bordered-minimal
                serviceIconBackgroundColor: 'transparent', // Kein Icon Background
                serviceIconBorderWidth: 1, // Schwarzer Border ums Icon
                serviceIconBorderRadius: 50, // Runder Icon Container
                serviceIconColor: '#000000', // Schwarze Icons
                // ✅ TEXT VISIBILITY: Titel einblenden, Subtitle nach Services
                showTitleText: true, // ✅ TITEL EINBLENDEN (oben)
                showServiceIcons: false, // Icons verstecken für clean design
                showSubtitleText: true, // Subtitle unter den Services anzeigen
                containerBorder: true // Container-Borders aktivieren
            },
            'bordered-minimal-clean': {
                // Clean Version ohne Header/Subtitle/Container-Borders - NUR die 4 Service-Container
                height: 'auto',
                heightMobile: 'auto',
                minHeight: 0,
                minHeightMobile: 0,
                paddingTop: 0,
                paddingTopMobile: 0,
                paddingBottom: 0,
                paddingBottomMobile: 0,
                servicesPerRow: 4,
                servicesPerRowMobile: 1,
                serviceLayout: 'bordered-minimal-clean',
                // ✅ IDENTISCHE SERVICE-SETTINGS wie bordered-minimal
                servicePadding: 40,
                servicePaddingHorizontal: 25,
                serviceSpacing: 0, // Kein Gap - nahtlose Borders (IDENTISCH)
                serviceBorderRadius: 0, // IDENTISCH
                serviceShadow: false, // IDENTISCH
                serviceBorder: true, // IDENTISCH
                serviceBorderColor: '#e5e5e5', // IDENTISCH
                serviceBackgroundColor: 'transparent', // IDENTISCH
                // Icon Settings - IDENTISCH zu bordered-minimal
                serviceIconBackgroundColor: 'transparent',
                serviceIconBorderWidth: 1,
                serviceIconBorderRadius: 50,
                serviceIconColor: '#000000',
                // ✅ CLEAN SETTINGS: Alles versteckt außer Services
                showServiceIcons: false, // Icons verstecken
                showTitleText: false, // ✅ TITEL VERSTECKT
                showSubtitleText: false, // ✅ SUBTITLE VERSTECKT
                containerBorder: false // ✅ CONTAINER-BORDERS ENTFERNT
            },
            'minimal-four': {
                // Minimal Four - 2 Services per Row mit Borders und Header/Subtitle
                height: 'auto',
                heightMobile: 'auto',
                minHeight: 400,
                minHeightMobile: 500,
                paddingTop: 60,
                paddingTopMobile: 40,
                paddingBottom: 0, // ✅ KEIN UNTERES PADDING
                paddingBottomMobile: 0, // ✅ KEIN UNTERES PADDING MOBILE
                servicesPerRow: 2, // ✅ 2 SPALTEN (statt 4 bei bordered-minimal)
                servicesPerRowMobile: 1,
                serviceLayout: 'minimal-four',
                // ✅ SERVICE-SETTINGS wie bordered-minimal aber für 2 Spalten optimiert
                servicePadding: 40,
                servicePaddingHorizontal: 25,
                serviceSpacing: 0, // Kein Gap - nahtlose Borders
                serviceBorderRadius: 0,
                serviceShadow: false,
                serviceBorder: true,
                serviceBorderColor: '#e5e5e5',
                serviceBackgroundColor: 'transparent',
                // Icon Settings
                serviceIconBackgroundColor: 'transparent',
                serviceIconBorderWidth: 1,
                serviceIconBorderRadius: 50,
                serviceIconColor: '#000000',
                // ✅ TEXT SETTINGS: Header und Subtitle sichtbar
                showServiceIcons: false, // Icons verstecken für clean design
                showTitleText: true, // ✅ TITEL SICHTBAR
                showSubtitleText: true, // ✅ SUBTITLE SICHTBAR
                containerBorder: true // ✅ CONTAINER-BORDERS AKTIVIERT
            },
            'clean-image-cards': {
                // Clean Image Cards - Basis classic-grid aber mit großen Images statt Icons
                height: 'auto',
                heightMobile: 'auto',
                minHeight: 500,
                minHeightMobile: 600,
                paddingTop: 60,
                paddingTopMobile: 40,
                paddingBottom: 60,
                paddingBottomMobile: 40,
                servicesPerRow: 2,
                servicesPerRowMobile: 1,
                serviceLayout: 'clean-image-cards',
                servicePadding: 32,
                serviceSpacing: 24,
                serviceBorderRadius: 0,
                // ✅ CLEAN SETTINGS: Kein Shadow, kein Border
                serviceShadow: false,
                serviceBorder: false,
                // ✅ GROSSE IMAGES: 240x330px Desktop, proportional kleiner Mobile
                serviceImageWidth: 240,
                serviceImageHeight: 330,
                serviceImageWidthMobile: 180,
                serviceImageHeightMobile: 250,
                // ✅ IMAGE SPACING: Standard Abstand für clean-image-cards
                serviceImageTopMargin: 0, // ✅ KEIN TOP MARGIN für clean-image-cards
                serviceImageTopMarginMobile: 0, // ✅ KEIN TOP MARGIN Mobile
                // ✅ ICON SETTINGS: Kantige Icons mit dickerem Border
                serviceIconBorderWidth: 2, // ✅ DICKER BORDER
                serviceIconBorderRadius: 0, // ✅ KANTIGE ICONS
                // ✅ BUTTON SETTINGS: Clean Design mit eckigen Buttons
                serviceButtonBorderRadius: 0, // ✅ ECKIGE BUTTONS
                serviceButtonBorder: true, // ✅ BUTTON BORDER AKTIVIERT
                serviceButtonBorderWidth: 2, // ✅ DICKER BUTTON BORDER
                serviceButtonShadow: false, // ✅ KEIN BUTTON SCHATTEN für clean design
                serviceButtonWidth: '100%', // ✅ VOLLE BREITE für clean design
                serviceButtonHeight: '48px', // ✅ FESTE HÖHE für clean design
                serviceButtonWidthPx: 200, // ✅ SLIDER-DEFAULT für clean design
                serviceButtonHeightPx: 48, // ✅ SLIDER-DEFAULT für clean design
                // ✅ BUTTON ACTIVATION: Buttons aktiviert für clean-image-cards
                showServiceButtons: true, // ✅ BUTTONS AKTIVIERT für clean-image-cards Layout
                // ✅ SERVICE VISIBILITY: Alle 4 Services sichtbar für clean-image-cards
                visibleServices: 4, // ✅ ALLE 4 SERVICES SICHTBAR
                // ✅ TEXT CONTAINER SETTINGS: Begrenzte Text-Breite für bessere Lesbarkeit
                serviceTextMaxWidth: 320, // ✅ BEGRENZTE TEXT-BREITE für clean design (Slider-Wert)
                serviceTextMaxWidthMobile: 280, // ✅ MOBILE TEXT-BREITE (Slider-Wert)
                // ✅ IMAGE MODE: Standardmäßig Images verwenden
                showServiceIcons: true, // Icons verfügbar aber Images bevorzugt
                invertIconColors: false,
                // ✅ TEXT VISIBILITY: Alle Texte sichtbar
                showTitleText: true,
                showSubtitleText: true,
                containerBorder: true
            },
            'three-column-clean': {
                // ✅ THREE COLUMN CLEAN - Wie Clean Image Cards aber 3 Services in einer Reihe
                height: 'auto',
                heightMobile: 'auto',
                minHeight: 500,
                minHeightMobile: 600,
                paddingTop: 60,
                paddingTopMobile: 40,
                paddingBottom: 60,
                paddingBottomMobile: 40,
                servicesPerRow: 3, // ✅ 3 SPALTEN Desktop
                servicesPerRowMobile: 1,
                serviceLayout: 'three-column-clean',
                servicePadding: 32,
                serviceSpacing: 24,
                serviceBorderRadius: 0,
                // ✅ CLEAN SETTINGS: Kein Shadow, kein Border
                serviceShadow: false,
                serviceBorder: false,
                // ✅ BRAND COLOR BACKGROUND: Service Cards verwenden Brand Color 1
                serviceBackgroundColor: colors?.[0] || '#171717', // ✅ BRAND COLOR 1 (erste Farbe aus colors array)
                // ✅ OPTIMIERTE CARD HEIGHTS: 580px für einheitliche Service-Cards
                serviceMinHeight: 580, // ✅ SERVICE CARD MIN HEIGHT: 580px für perfekte 3-Spalten-Symmetrie
                // ✅ OPTIMIERTE IMAGES: 240x210px für 3-Spalten-Layout
                serviceImageWidth: 240,
                serviceImageHeight: 210, // ✅ NEUE HÖHE: 210px (statt 330px)
                serviceImageWidthMobile: 180,
                serviceImageHeightMobile: 160, // ✅ PROPORTIONAL: 160px Mobile
                // ✅ ICON SETTINGS: Kantige Icons mit dickerem Border
                serviceIconBorderWidth: 2,
                serviceIconBorderRadius: 0,
                // ✅ BUTTON SETTINGS: Weiße Buttons mit schwarzer Schrift und perfekter Rundung
                serviceButtonBorderRadius: 50, // ✅ PERFEKTE RUNDUNG (50px)
                serviceButtonBorder: true, // ✅ BUTTON BORDER AKTIVIERT
                serviceButtonBorderWidth: 2, // ✅ 2PX BORDER
                serviceButtonShadow: false, // ✅ KEIN SCHATTEN für clean design
                serviceButtonColor: '#ffffff', // ✅ WEISSE BACKGROUND
                serviceButtonTextColor: '#000000', // ✅ SCHWARZE SCHRIFT
                serviceButtonBorderColor: '#000000', // ✅ SCHWARZER BORDER
                serviceButtonWidthPx: 140, // ✅ ANGEPASSTE BUTTON-BREITE für 3 Spalten
                serviceButtonHeightPx: 44, // ✅ STANDARD BUTTON-HÖHE
                // ✅ TEXT CONTAINER SETTINGS: Optimiert für einheitliche Button-Höhen
                serviceTextMaxWidth: 280, // ✅ SCHMALER für 3 Spalten
                serviceTextMaxWidthMobile: 260, // ✅ MOBILE TEXT-BREITE
                serviceTextMinHeight: 180, // ✅ TEXT MIN HEIGHT: 180px für einheitliche Button-Platzierung
                serviceTextMinHeightMobile: 160, // ✅ MOBILE TEXT MIN HEIGHT: 160px
                // ✅ DESCRIPTION MIN HEIGHT: Spezifische Kontrolle für P-Tag
                serviceDescriptionMinHeight: 80, // ✅ DESCRIPTION MIN HEIGHT: 80px für Button-Alignment
                serviceDescriptionMinHeightMobile: 70, // ✅ MOBILE DESCRIPTION MIN HEIGHT: 70px
                // ✅ IMAGE SPACING: Erhöhter Abstand für bessere Optik
                serviceImageTopMargin: 20, // ✅ IMAGE TOP MARGIN: 20px für three-column-clean
                serviceImageTopMarginMobile: 16, // ✅ MOBILE IMAGE TOP MARGIN: 16px
                // ✅ BUTTON ACTIVATION: Buttons aktiviert für three-column-clean
                showServiceButtons: true, // ✅ BUTTONS AKTIVIERT für three-column-clean Layout
                // ✅ IMAGE MODE: Standardmäßig Images verwenden
                showServiceIcons: true, // Icons verfügbar aber Images bevorzugt
                invertIconColors: false,
                // ✅ TEXT VISIBILITY: Alle Texte sichtbar
                showTitleText: true,
                showSubtitleText: true,
                containerBorder: true
            }
        };
        
        const variations = layoutVariations[currentLayout];
        if (variations) {
            console.log(`🎯 Applying layout variations for ${currentLayout}:`, variations);
            
            // ✅ PRESERVE ICON VISIBILITY: Behalte bestehende Icon-Settings wenn User sie geändert hat
            const preservedIconSettings = {};
            
            // ✅ FIX: Prüfe ob User bereits Icon-Settings angepasst hat ODER verwende Template-Default
            const currentIconSetting = editData.showServiceIcons !== undefined ? 
                editData.showServiceIcons : 
                true; // Template-Default aus widgetTemplates.js
            
            preservedIconSettings.showServiceIcons = currentIconSetting;
            console.log('🎨 Icon visibility setting:', {
                fromEditData: editData.showServiceIcons,
                templateDefault: true,
                finalSetting: currentIconSetting
            });
            
            // ✅ SMART MERGE: Nur überschreiben wenn Layout es explizit braucht (bordered-minimal, minimal-cards)
            const smartVariations = { ...variations };
            
            // Für Layouts die Icons explizit deaktivieren: Icons müssen aus sein
            const layoutsWithoutIcons = ['bordered-minimal', 'minimal-cards', 'bordered-minimal-clean', 'minimal-four'];
            if (!layoutsWithoutIcons.includes(currentLayout) && editData.showServiceIcons !== undefined) {
                delete smartVariations.showServiceIcons; // Nicht überschreiben
                console.log('🎨 Preserving icon visibility for layouts with icons');
            }
            
            // ✅ CLEAN IMAGE CARDS: Alle Services auf Image-Modus stellen
            if (currentLayout === 'clean-image-cards') {
                const currentServices = editData.services || [];
                const updatedServices = currentServices.map(service => ({
                    ...service,
                    useImage: true // ✅ Alle Services auf Image-Modus
                }));
                
                console.log('🖼️ Setting all services to image mode for clean-image-cards layout');
                smartVariations.services = updatedServices;
            }
            
            // ✅ SERVICE VISIBILITY MANAGEMENT: Layout-spezifische Service-Anzahl
            const currentServices = editData.services || [];
            
            if (currentLayout === 'three-column-clean') {
                // ✅ THREE COLUMN CLEAN: Nur erste 3 Services sichtbar, Image-Modus aktiviert
                const updatedServices = currentServices.map((service, index) => ({
                    ...service,
                    useImage: true, // ✅ Alle Services auf Image-Modus
                    visible: index < 3 // ✅ Nur erste 3 Services sichtbar
                }));
                
                console.log('🏛️ Setting first 3 services visible and image mode for three-column-clean layout');
                smartVariations.services = updatedServices;
            } else {
                // ✅ ALLE ANDEREN LAYOUTS: Alle 4 Services sichtbar
                const updatedServices = currentServices.map((service, index) => ({
                    ...service,
                    visible: index < 4 // ✅ Alle 4 Services sichtbar
                }));
                
                console.log(`📋 Setting all 4 services visible for ${currentLayout} layout`);
                smartVariations.services = updatedServices;
            }
            
            // ✅ CRITICAL: Für bordered-minimal-clean müssen Texte IMMER ausgeblendet werden
            // Für alle anderen Layouts müssen Texte IMMER eingeblendet werden
            if (currentLayout === 'bordered-minimal-clean') {
                // Clean layout: Texte müssen aus sein
                smartVariations.showTitleText = false;
                smartVariations.showSubtitleText = false;
                smartVariations.containerBorder = false;
                console.log('🎨 Forcing text visibility OFF for bordered-minimal-clean');
            } else {
                // Alle anderen Layouts: Texte müssen an sein (falls vorher ausgeblendet)
                smartVariations.showTitleText = true;
                smartVariations.showSubtitleText = true;
                smartVariations.containerBorder = true;
                console.log('🎨 Forcing text visibility ON for layout:', currentLayout);
            }
            
            // ✅ KRITISCH: BATCH UPDATE mit preserved settings
            const updatedEditData = { 
                ...editData, 
                ...smartVariations,
                ...preservedIconSettings  // ✅ Immer die User-Icon-Settings behalten
            };
            
            console.log(`🔧 Batch updating editData:`, {
                before: { 
                    height: editData.height, 
                    heightMobile: editData.heightMobile,
                    layoutType: editData.layoutType,
                    serviceLayout: editData.serviceLayout,
                    showServiceIcons: editData.showServiceIcons
                },
                after: { 
                    height: updatedEditData.height, 
                    heightMobile: updatedEditData.heightMobile,
                    layoutType: updatedEditData.layoutType,
                    serviceLayout: updatedEditData.serviceLayout,
                    showServiceIcons: updatedEditData.showServiceIcons
                }
            });
            
            // ✅ FIREBASE-FIX: Sicherstellen dass position nie undefined ist
            const safePosition = widget.position !== undefined && widget.position !== null ? widget.position : 0;
            
            // ✅ DIREKTE onUpdate statt einzelne handleInputChange calls
            onUpdate({ ...updatedEditData, position: safePosition });
            
            console.log(`✅ SimplisticServices layout variations applied via batch update with preserved icon settings`);
        }
    }, [editData.layoutType, isInitialized, previousLayoutType, editData, onUpdate]);

    // ✅ LAYOUT CHANGE HANDLER
    const handleLayoutChange = (layoutId, layoutConfig) => {
        console.log(`🎨 SimplisticServices layout change: ${layoutId}`, layoutConfig);
        const fakeEvent = {
            target: { name: 'layoutType', value: layoutId, type: 'text' }
        };
        handleInputChange(fakeEvent);
    };

    // ✅ SEPARATE UPLOAD STATES für jedes Service-Image (wie HeroHeader)
    const [uploadingStates, setUploadingStates] = useState({});
    const [iconPickerStates, setIconPickerStates] = useState({});
    const [showImageTooBigPopup, setShowImageTooBigPopup] = useState(false);
    const [tooBigFileInfo, setTooBigFileInfo] = useState({ size: 0, name: '' });
    
    // ✅ INDIVIDUAL UPLOAD STATES für Services (wie HeroHeader Pattern)
    const [isUploadingServiceImage0, setIsUploadingServiceImage0] = useState(false);
    const [isUploadingServiceImage1, setIsUploadingServiceImage1] = useState(false);
    const [isUploadingServiceImage2, setIsUploadingServiceImage2] = useState(false);
    const [isUploadingServiceImage3, setIsUploadingServiceImage3] = useState(false);
    const [isUploadingServiceImage4, setIsUploadingServiceImage4] = useState(false);
    const [isUploadingServiceImage5, setIsUploadingServiceImage5] = useState(false);
    
    // Helper function to get upload state for service index
    const getServiceUploadState = (index) => {
        const states = [
            isUploadingServiceImage0,
            isUploadingServiceImage1, 
            isUploadingServiceImage2,
            isUploadingServiceImage3,
            isUploadingServiceImage4,
            isUploadingServiceImage5
        ];
        return states[index] || false;
    };
    
    // Helper function to set upload state for service index
    const setServiceUploadState = (index, isUploading) => {
        const setters = [
            setIsUploadingServiceImage0,
            setIsUploadingServiceImage1,
            setIsUploadingServiceImage2, 
            setIsUploadingServiceImage3,
            setIsUploadingServiceImage4,
            setIsUploadingServiceImage5
        ];
        const setter = setters[index];
        if (setter) {
            setter(isUploading);
        }
    };
    
    const setUploadingState = (index, isUploading) => {
        setUploadingStates(prev => ({
            ...prev,
            [index]: isUploading
        }));
    };

    const setIconPickerState = (index, isOpen) => {
        console.log('🎯 setIconPickerState called:', { index, isOpen, currentState: iconPickerStates[index] });
        setIconPickerStates(prev => {
            const newState = {
                ...prev,
                [index]: isOpen
            };
            console.log('📊 Icon picker states updated:', newState);
            return newState;
        });
    };

    // Services verwalten
    const services = editData.services || [];
    
    const addService = () => {
        console.log('➕ addService called - adding new service');
        const newServices = [...services, {
            name: '',
            description: '',
            price: '', // ✅ PRICE-FELD hinzugefügt
            icon: '/img/icons/handdrawn/star.svg',
            iconType: 'picker',
            customIcon: '',
            visible: true, // ✅ NEUE SERVICES sind standardmäßig sichtbar
            useImage: false, // ✅ NEUES SYSTEM: Standard ist Icon-Modus
            serviceImage: '', // ✅ SERVICE IMAGE URL
            // ✅ BUTTON SYSTEM - NEU
            buttonText: 'Learn More', // Default Button-Text
            buttonLink: '#', // Default Button-Link
            showButton: true // Button standardmäßig sichtbar
        }];
        
        // ✅ DIRECT UPDATE für sofortige Live-Aktualisierung
        const updatedData = { ...editData, services: newServices };
        setEditData(updatedData);
        onUpdate(updatedData);
        console.log('✅ New service added, total services:', newServices.length);
    };

    const removeService = (index) => {
        console.log('🗑️ removeService called for index:', index);
        const newServices = services.filter((_, i) => i !== index);
        
        // ✅ DIRECT UPDATE für sofortige Live-Aktualisierung
        const updatedData = { ...editData, services: newServices };
        setEditData(updatedData);
        onUpdate(updatedData);
        console.log('✅ Service removed, remaining services:', newServices.length);
    };

    const updateService = (index, field, value) => {
        console.log('🔧 updateService called:', { 
            index, 
            field, 
            value, 
            currentServices: services.length,
            beforeUpdate: services[index]
        });
        
        const newServices = [...services];
        newServices[index] = { ...newServices[index], [field]: value };
        
        // ✅ CRITICAL FIX: Update WIDGET direkt, nicht nur editData
        const updatedWidgetData = { ...widget, services: newServices };
        
        console.log('📡 DIRECT WIDGET UPDATE:', { 
            field, 
            newValue: value, 
            serviceIndex: index,
            totalServices: newServices.length,
            updatedService: newServices[index],
            fullWidgetData: updatedWidgetData
        });
        
        // ✅ BYPASS useWidgetEdit: Direkte Widget-Update um Race Conditions zu vermeiden
        onUpdate(updatedWidgetData);
        
        // ✅ CONDITIONAL SYNC: Nur editData updaten wenn nicht bereits durch Upload-Callback gemacht
        const currentEditDataServices = editData.services || [];
        const needsEditDataUpdate = !currentEditDataServices[index] || 
                                   currentEditDataServices[index][field] !== value;
        
        if (needsEditDataUpdate) {
            const updatedEditData = { ...editData, services: newServices };
            setEditData(updatedEditData);
            console.log('🔄 EditData synced after service update');
        } else {
            console.log('⏭️ EditData already up-to-date, skipping sync');
        }
        
        console.log('✅ Service update sent directly to widget:', {
            updatedService: newServices[index],
            totalServices: newServices.length,
            field: field,
            value: value
        });
    };

    // ✅ KORRIGIERTER ICON UPLOAD - Direkter Firebase Upload wie Service Images
    const handleServiceIconUpload = async (index, file) => {
        if (!file) return;
        
        if (file.size > 2 * 1024 * 1024) {
            setTooBigFileInfo({ size: file.size, name: file.name });
            setShowImageTooBigPopup(true);
            return;
        }
        
        setUploadingState(index, true);
        
        try {
            // ✅ DIREKTER FIREBASE UPLOAD - EXAKT WIE SERVICE IMAGES
            const { getStorage, ref: storageRef, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
            const { getAuth } = await import('firebase/auth');
            
            const storage = getStorage();
            const auth = getAuth();
            const userId = auth.currentUser.uid;
            const fieldName = `service_${index}_icon`;
            const imageRef = storageRef(storage, `pages/${userId}/${widget.id}/${fieldName}_${file.name}`);
            const uploadTask = uploadBytesResumable(imageRef, file);

            uploadTask.on('state_changed', 
                (snapshot) => {
                    // Upload progress
                }, 
                (error) => {
                    console.error('❌ Icon upload error:', error);
                    setUploadingState(index, false);
                    alert('Upload failed. Please try again.');
                }, 
                () => {
                    getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                        console.log('📥 Firebase upload completed, updating service:', {
                            index,
                            downloadURL,
                            beforeUpdate: services[index]
                        });
                        
                        // ✅ BATCH UPDATE: Beide Werte gleichzeitig setzen um Race Conditions zu vermeiden
                        const newServices = [...services];
                        newServices[index] = { 
                            ...newServices[index], 
                            customIcon: downloadURL,
                            iconType: 'upload'
                        };
                        
                        // ✅ DIRECT WIDGET UPDATE mit beiden Werten
                        const updatedWidgetData = { ...widget, services: newServices };
                        onUpdate(updatedWidgetData);
                        
                        // ✅ SYNC EDITDATA
                        const updatedEditData = { ...editData, services: newServices };
                        setEditData(updatedEditData);
                        
                        setUploadingState(index, false);
                        console.log('✅ Service icon upload completed with batch update:', {
                            downloadURL,
                            updatedService: newServices[index],
                            iconType: newServices[index].iconType,
                            customIcon: newServices[index].customIcon
                        });
                    });
                }
            );
        } catch (error) {
            console.error('❌ Firebase import error:', error);
            setUploadingState(index, false);
            alert('Upload failed. Please try again.');
        }
    };

    // ✅ DIREKTER FIREBASE UPLOAD - WIE TESTIMONIALS
    const handleServiceImageUpload = async (index, file) => {
        if (!file) return;
        
        console.log('📤 Service image upload started:', { 
            index, 
            fileName: file.name, 
            fileSize: file.size
        });
        
        // Size check
        if (file.size > 2 * 1024 * 1024) {
            setTooBigFileInfo({ size: file.size, name: file.name });
            setShowImageTooBigPopup(true);
            return;
        }

        setServiceUploadState(index, true);
        
        try {
            // ✅ DIREKTER FIREBASE UPLOAD - EXAKT WIE TESTIMONIALS
            const { getStorage, ref: storageRef, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
            const { getAuth } = await import('firebase/auth');
            
            const storage = getStorage();
            const auth = getAuth();
            const userId = auth.currentUser.uid;
            const fieldName = `service_${index}_image`;
            const imageRef = storageRef(storage, `pages/${userId}/${widget.id}/${fieldName}_${file.name}`);
            const uploadTask = uploadBytesResumable(imageRef, file);

            uploadTask.on('state_changed', 
                (snapshot) => {
                    // Upload progress
                }, 
                (error) => {
                    console.error('Upload error:', error);
                    setServiceUploadState(index, false);
                    alert('Upload failed. Please try again.');
                }, 
                () => {
                    getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                        updateService(index, 'serviceImage', downloadURL);
                        setServiceUploadState(index, false);
                        console.log('✅ Service image uploaded:', downloadURL);
                    });
                }
            );
        } catch (error) {
            console.error('Firebase import error:', error);
            alert('Upload failed. Please try again.');
            setServiceUploadState(index, false);
        }
    };

    // Icon Picker Handler
    const handleIconSelect = (index, selectedIcon) => {
        console.log('🎨 handleIconSelect called:', { 
            index, 
            selectedIcon: selectedIcon ? { id: selectedIcon.id, src: selectedIcon.src, alt: selectedIcon.alt } : null,
            currentService: services[index]
        });
        
        if (selectedIcon) {
            console.log('✅ Updating service with selected icon:', selectedIcon.src);
            updateService(index, 'icon', selectedIcon.src);
            updateService(index, 'iconType', 'picker');
            updateService(index, 'customIcon', ''); // Clear custom icon
        } else {
            console.log('❌ No icon selected or icon deselected');
        }
        setIconPickerState(index, false);
    };

    // 🚀 REVOLUTIONARY TEXT SETTINGS - Titel und Untertitel mit Show/Hide Toggles
    const textConfig = [
        {
            fieldName: 'titleText',
            label: 'Main Title (H2)',
            type: 'input',
            placeholder: 'Our Services',
            fontField: 'titleFont',
            fontType: 'big',
            effectField: 'titleEffect'
        },
        // ✅ NEU: Highlight Words Feld hinzufügen
        {
            fieldName: 'titleHighlightWords',
            label: 'Title - Highlight Words',
            type: 'input',
            placeholder: 'Services, Professional (comma separated)',
            description: 'Enter words to highlight with different styling',
            fontField: 'titleHighlightFont',
            fontType: 'small', // ✅ Anderer Font-Pool als Main Title
            effectField: 'titleHighlightEffect'
        },
        {
            fieldName: 'subtitleText',
            label: 'Subtitle Text (P)',
            type: 'textarea',
            placeholder: 'Discover our comprehensive range of professional services...',
            rows: 3,
            fontField: 'subtitleFont',
            fontType: 'small',
            effectField: 'subtitleEffect'
        }
    ];

    // Color Configuration für MoreSettingsSection
    const colorConfig = [
        [
            {
                fieldName: 'titleColor',
                label: 'Title',
                defaultValue: '#000000',
                icon: '/img/text-fat.svg'
            },
            {
                fieldName: 'subtitleColor',
                label: 'Subtitle',
                defaultValue: '#666666',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'backgroundColor',
                label: 'Background',
                defaultValue: '#ffffff',
                icon: '/img/background.svg'
            }
        ],
        [
            {
                fieldName: 'serviceNameColor',
                label: 'Service Name',
                defaultValue: '#000000',
                icon: '/img/text-fat.svg'
            },
            {
                fieldName: 'serviceDescriptionColor',
                label: 'Service Description',
                defaultValue: '#666666',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'servicePriceColor',
                label: 'Service Price',
                defaultValue: '#10b981',
                icon: '/img/text-fat.svg'
            },
            {
                fieldName: 'serviceIconColor',
                label: 'Service Icon',
                defaultValue: '#667eea',
                icon: '/img/icons.svg'
            },
            {
                fieldName: 'titleHighlightColor',
                label: 'Title Highlight',
                defaultValue: '#D4A574',
                icon: '/img/text-fat.svg'
            }
        ],
        [
            {
                fieldName: 'serviceIconBackgroundColor',
                label: 'Icon Background',
                defaultValue: 'rgba(102, 126, 234, 0.1)',
                icon: '/img/background.svg'
            },
            {
                fieldName: 'serviceBackgroundColor',
                label: 'Service Card Background',
                defaultValue: '#ffffff',
                icon: '/img/background.svg'
            },
            {
                fieldName: 'serviceBorderColor',
                label: 'Service Border Color',
                defaultValue: '#e5e5e5',
                icon: '/img/element.svg'
            },
            {
                fieldName: 'containerBorderColor',
                label: 'Container Border Color',
                defaultValue: '#e5e5e5',
                icon: '/img/background.svg'
            }
        ],
        [
            {
                fieldName: 'serviceButtonColor',
                label: 'Button Background',
                defaultValue: '#667eea',
                icon: '/img/button.svg'
            },
            {
                fieldName: 'serviceButtonTextColor',
                label: 'Button Text',
                defaultValue: '#ffffff',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'serviceButtonBorderColor',
                label: 'Button Border',
                defaultValue: '#667eea',
                icon: '/img/border.svg'
            },
            {
                fieldName: 'serviceButtonHoverColor',
                label: 'Button Hover',
                defaultValue: '#5a67d8',
                icon: '/img/button.svg'
            }
        ]
    ];

    // Font Settings Configuration für MoreSettingsSection
    const fontConfig = [
        {
            label: 'Title',
            sizeField: 'titleSize',
            weightField: 'titleWeight',
            defaultSize: '32',
            defaultWeight: '600'
        },
        // ✅ NEU: Highlight Font Settings hinzufügen
        {
            label: 'Title - Highlight Words',
            sizeField: 'titleHighlightSize',
            weightField: 'titleHighlightWeight',
            defaultSize: '32', // Gleich wie Title
            defaultWeight: '800'
        },
        {
            label: 'Subtitle',
            sizeField: 'subtitleSize',
            weightField: 'subtitleWeight',
            defaultSize: '18',
            defaultWeight: '400'
        },
        {
            label: 'Service Name',
            sizeField: 'serviceNameSize',
            weightField: 'serviceNameWeight',
            defaultSize: '20',
            defaultWeight: '600'
        },
        {
            label: 'Service Description',
            sizeField: 'serviceDescriptionSize',
            weightField: 'serviceDescriptionWeight',
            defaultSize: '16',
            defaultWeight: '400'
        },
        {
            label: 'Service Button',
            sizeField: 'serviceButtonSize',
            weightField: 'serviceButtonWeight',
            defaultSize: '14',
            defaultWeight: '500'
        }
    ];

    // 🚀 REVOLUTIONARY LAYOUT SETTINGS - Custom Properties für Simplistic Services Widget
    const customLayoutProperties = [
        {
            section: 'Content Settings',
            icon: 'fa-align-left',
            properties: [
                {
                    id: 'maxWidth',
                    label: 'Widget Max Width',
                    min: 800,
                    max: 1600,
                    step: 50,
                    unit: 'px',
                    icon: '📄',
                    description: 'Maximum width of the widget content'
                },
                {
                    id: 'subtitleMaxWidth',
                    label: 'Subtitle Max Width',
                    min: 400,
                    max: 1200,
                    step: 50,
                    unit: 'px',
                    icon: '📝',
                    description: 'Maximum width of subtitle text'
                },
                {
                    id: 'contentSpacing',
                    label: 'Content Spacing',
                    min: 20,
                    max: 80,
                    step: 5,
                    unit: 'px',
                    icon: '📄',
                    description: 'Space between header and services'
                }
            ]
        },
        {
            section: 'Service Image Settings',
            icon: 'fa-image',
            properties: [
                {
                    id: 'serviceImageWidth',
                    label: 'Image Width',
                    min: 40,
                    max: 600,
                    step: 10,
                    unit: 'px',
                    icon: '🖼️',
                    description: 'Width of service image containers',
                    defaultValue: 60
                },
                {
                    id: 'serviceImageHeight',
                    label: 'Image Height',
                    min: 40,
                    max: 600,
                    step: 10,
                    unit: 'px',
                    icon: '🖼️',
                    description: 'Height of service image containers',
                    defaultValue: 60
                }
            ]
        },
        {
            section: 'Service Settings',
            icon: 'fa-th',
            properties: [
                {
                    id: 'serviceSpacing',
                    label: 'Service Spacing',
                    min: 0,
                    max: 60,
                    step: 4,
                    unit: 'px',
                    icon: '📋',
                    description: 'Space between service cards'
                },
                {
                    id: 'servicePadding',
                    label: 'Service Padding',
                    min: 16,
                    max: 60,
                    step: 4,
                    unit: 'px',
                    icon: '📦',
                    description: 'Inner padding of service cards'
                },
                {
                    id: 'servicesPerRow',
                    label: 'Services per Row',
                    min: 1,
                    max: 4,
                    step: 1,
                    unit: '',
                    icon: '📋',
                    description: 'Number of services per row on desktop'
                },
                {
                    id: 'servicesPerRowMobile',
                    label: 'Services per Row (Mobile)',
                    min: 1,
                    max: 2,
                    step: 1,
                    unit: '',
                    icon: '📱',
                    description: 'Number of services per row on mobile'
                }
            ]
        },
        {
            section: 'Button Size Settings',
            icon: 'fa-expand-arrows-alt',
            properties: [
                {
                    id: 'serviceButtonWidth',
                    label: 'Button Width',
                    type: 'text',
                    icon: '↔️',
                    description: 'Button width (auto, 120px, 100%, fit-content)',
                    defaultValue: 'auto',
                    placeholder: 'auto, 120px, 100%...'
                },
                {
                    id: 'serviceButtonWidthMobile',
                    label: 'Button Width (Mobile)',
                    type: 'text',
                    icon: '📱',
                    description: 'Button width on mobile devices',
                    defaultValue: 'auto',
                    placeholder: 'auto, 100%, 140px...'
                },
                {
                    id: 'serviceButtonHeight',
                    label: 'Button Height',
                    type: 'text',
                    icon: '↕️',
                    description: 'Button height (auto, 48px, 3rem)',
                    defaultValue: 'auto',
                    placeholder: 'auto, 48px, 3rem...'
                },
                {
                    id: 'serviceButtonHeightMobile',
                    label: 'Button Height (Mobile)',
                    type: 'text',
                    icon: '📱',
                    description: 'Button height on mobile devices',
                    defaultValue: 'auto',
                    placeholder: 'auto, 44px, 2.5rem...'
                },
                {
                    id: 'serviceButtonWidthPx',
                    label: 'Button Width (Pixel Slider)',
                    min: 60,
                    max: 400,
                    step: 5,
                    unit: 'px',
                    icon: '📏',
                    description: 'Button width in pixels (for precise control)',
                    defaultValue: 120
                },
                {
                    id: 'serviceButtonWidthPxMobile',
                    label: 'Button Width Mobile (Pixel Slider)',
                    min: 60,
                    max: 300,
                    step: 5,
                    unit: 'px',
                    icon: '📱',
                    description: 'Button width in pixels on mobile',
                    defaultValue: 120
                },
                {
                    id: 'serviceButtonHeightPx',
                    label: 'Button Height (Pixel Slider)',
                    min: 24,
                    max: 100,
                    step: 2,
                    unit: 'px',
                    icon: '📐',
                    description: 'Button height in pixels (for precise control)',
                    defaultValue: 44
                },
                {
                    id: 'serviceButtonHeightPxMobile',
                    label: 'Button Height Mobile (Pixel Slider)',
                    min: 24,
                    max: 80,
                    step: 2,
                    unit: 'px',
                    icon: '📱',
                    description: 'Button height in pixels on mobile',
                    defaultValue: 40
                }
            ]
        }
    ];

    // 🔧 ADVANCED SETTINGS CONFIGURATION - Flache Struktur für AdvancedSettingsSection
    const advancedConfig = [
        // ========================================
        // 📄 CONTENT & LAYOUT SETTINGS
        // ========================================
        {
            fieldName: 'contentSpacing',
            label: 'Content Side Spacing',
            type: 'slider',
            defaultValue: 40,
            min: 1,
            max: 100,
            step: 5,
            unit: 'px',
            color: '#10b981',
            icon: '/img/element.svg',
            description: 'Side spacing around service components (prevents touching edges)'
        },
        {
            fieldName: 'contentSpacingMobile',
            label: 'Content Side Spacing Mobile',
            type: 'slider',
            defaultValue: 20,
            min: 1,
            max: 60,
            step: 5,
            unit: 'px',
            color: '#10b981',
            icon: '/img/element.svg',
            description: 'Side spacing around service components on mobile'
        },
        {
            fieldName: 'titleBottomMargin',
            label: 'Title Bottom Margin',
            type: 'slider',
            defaultValue: 16,
            min: 0,
            max: 60,
            step: 2,
            unit: 'px',
            color: '#8b5cf6',
            icon: '/img/text-fat.svg',
            description: 'Space between title and subtitle text'
        },
        {
            fieldName: 'serviceMinHeight',
            label: 'Service Card Min Height',
            type: 'slider',
            defaultValue: 280,
            min: 200,
            max: 1000,
            step: 10,
            unit: 'px',
            color: '#8b5cf6',
            icon: '/img/element.svg',
            description: 'Minimum height for service cards (ensures consistent sizing)'
        },
        {
            fieldName: 'serviceTextMaxWidth',
            label: 'Service Text Max Width',
            type: 'slider',
            defaultValue: 0,
            min: 0,
            max: 600,
            step: 10,
            unit: 'px',
            icon: '/img/text.svg',
            color: '#10b981',
            description: 'Maximum width of service text container (0 = unlimited)'
        },
        {
            fieldName: 'serviceTextMaxWidthMobile',
            label: 'Service Text Max Width (Mobile)',
            type: 'slider',
            defaultValue: 0,
            min: 0,
            max: 400,
            step: 10,
            unit: 'px',
            icon: '/img/text.svg',
            color: '#10b981',
            description: 'Maximum width of service text container on mobile (0 = unlimited)'
        },
        {
            fieldName: 'serviceTextMinHeight',
            label: 'Service Text Min Height',
            type: 'slider',
            defaultValue: 0,
            min: 0,
            max: 300,
            step: 10,
            unit: 'px',
            icon: '/img/text.svg',
            color: '#10b981',
            description: 'Minimum height of service text container (0 = auto height)'
        },
        {
            fieldName: 'serviceTextMinHeightMobile',
            label: 'Service Text Min Height (Mobile)',
            type: 'slider',
            defaultValue: 0,
            min: 0,
            max: 250,
            step: 10,
            unit: 'px',
            icon: '/img/text.svg',
            color: '#10b981',
            description: 'Minimum height of service text container on mobile (0 = auto height)'
        },
        {
            fieldName: 'serviceDescriptionMinHeight',
            label: 'Description Min Height',
            type: 'slider',
            defaultValue: 0,
            min: 0,
            max: 200,
            step: 5,
            unit: 'px',
            icon: '/img/text.svg',
            color: '#f59e0b',
            description: 'Minimum height for description text (ensures button alignment, 0 = auto)'
        },
        {
            fieldName: 'serviceDescriptionMinHeightMobile',
            label: 'Description Min Height (Mobile)',
            type: 'slider',
            defaultValue: 0,
            min: 0,
            max: 150,
            step: 5,
            unit: 'px',
            icon: '/img/text.svg',
            color: '#f59e0b',
            description: 'Minimum height for description text on mobile (0 = auto)'
        },
        {
            fieldName: 'serviceSpacing',
            label: 'Service Card Gap',
            type: 'slider',
            defaultValue: 24,
            min: 0,
            max: 60,
            step: 4,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#9c27b0',
            description: 'Gap between service cards in the grid'
        },
        {
            fieldName: 'servicePaddingHorizontal',
            label: 'Service Card Side Padding',
            type: 'slider',
            defaultValue: 32,
            min: 8,
            max: 80,
            step: 4,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#673ab7',
            description: 'Left and right padding inside service cards'
        },

        // ========================================
        // 🔘 BUTTONS
        // ========================================
        {
            fieldName: 'showServiceButtons',
            label: 'Show Service Buttons',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/button.svg',
            description: 'Toggle visibility of service buttons'
        },
        {
            fieldName: 'serviceButtonWidthPx',
            label: 'Button Width',
            type: 'slider',
            defaultValue: 120,
            min: 60,
            max: 300,
            step: 5,
            unit: 'px',
            icon: '/img/button.svg',
            color: '#667eea',
            description: 'Button width in pixels'
        },
        {
            fieldName: 'serviceButtonHeightPx',
            label: 'Button Height',
            type: 'slider',
            defaultValue: 44,
            min: 24,
            max: 80,
            step: 2,
            unit: 'px',
            icon: '/img/button.svg',
            color: '#667eea',
            description: 'Button height in pixels'
        },
        {
            fieldName: 'serviceButtonPadding',
            label: 'Button Vertical Padding',
            type: 'slider',
            defaultValue: 12,
            min: 0,
            max: 32,
            step: 1,
            unit: 'px',
            icon: '/img/button.svg',
            color: '#667eea',
            description: 'Top and bottom padding inside buttons (0px = kein Padding)'
        },
        {
            fieldName: 'serviceButtonPaddingHorizontal',
            label: 'Button Horizontal Padding',
            type: 'slider',
            defaultValue: 24,
            min: 0,
            max: 64,
            step: 2,
            unit: 'px',
            icon: '/img/button.svg',
            color: '#667eea',
            description: 'Left and right padding inside buttons (0px = kein Padding)'
        },
        {
            fieldName: 'serviceButtonBorderRadius',
            label: 'Button Border Radius',
            type: 'slider',
            defaultValue: 8,
            min: 0,
            max: 50,
            step: 1,
            unit: 'px',
            icon: '/img/button.svg',
            color: '#667eea',
            description: 'Border radius of service buttons (0px = eckig, 50px = sehr rund)'
        },
        {
            fieldName: 'serviceButtonBorder',
            label: 'Button Border',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/border.svg'
        },
        {
            fieldName: 'serviceButtonBorderWidth',
            label: 'Button Border Width',
            type: 'slider',
            defaultValue: 1,
            min: 0,
            max: 8,
            step: 1,
            unit: 'px',
            icon: '/img/border.svg',
            color: '#667eea',
            description: 'Width of button borders (0px = kein Border)'
        },
        {
            fieldName: 'serviceButtonHoverEffect',
            label: 'Button Hover Effects',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/button.svg'
        },
        {
            fieldName: 'serviceButtonShadow',
            label: 'Button Shadow',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/button.svg'
        },

        // ========================================
        // 🎨 ICONS & IMAGES SETTINGS
        // ========================================
        {
            fieldName: 'showServiceIcons',
            label: 'Show Service Icons',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/icons.svg',
            description: 'Toggle visibility of service icons'
        },
        {
            fieldName: 'invertIconColors',
            label: 'Smart Icon Inversion',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'serviceIconSize',
            label: 'Icon Size',
            type: 'slider',
            defaultValue: 60,
            min: 40,
            max: 100,
            step: 4,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#3F51B5',
            description: 'Size of service icons'
        },
        {
            fieldName: 'serviceIconBorderRadius',
            label: 'Icon Border Radius',
            type: 'slider',
            defaultValue: 12,
            min: 0,
            max: 100,
            step: 2,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#009688',
            description: 'Border radius of icon containers'
        },
        {
            fieldName: 'serviceIconPadding',
            label: 'Icon Padding',
            type: 'slider',
            defaultValue: 16,
            min: 8,
            max: 32,
            step: 2,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#FF9800',
            description: 'Padding inside icon containers'
        },
        {
            fieldName: 'serviceIconBorderWidth',
            label: 'Icon Border Width',
            type: 'slider',
            defaultValue: 0,
            min: 0,
            max: 5,
            step: 1,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#795548',
            description: 'Border width around icon containers'
        },
        {
            fieldName: 'serviceImageShadow',
            label: 'Image Shadow',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/background.svg',
            description: 'Add shadow to service images'
        },
        {
            fieldName: 'serviceImageTopMargin',
            label: 'Image Top Margin',
            type: 'slider',
            defaultValue: 0,
            min: 0,
            max: 50,
            step: 2,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#06b6d4',
            description: 'Top margin above service images (0 = no margin)'
        },
        {
            fieldName: 'serviceImageTopMarginMobile',
            label: 'Image Top Margin (Mobile)',
            type: 'slider',
            defaultValue: 0,
            min: 0,
            max: 40,
            step: 2,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#06b6d4',
            description: 'Top margin above service images on mobile (0 = no margin)'
        },

        // ========================================
        // 🎭 APPEARANCE & BEHAVIOR SETTINGS
        // ========================================
        {
            fieldName: 'showServicePrices',
            label: 'Show Service Prices',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/text.svg',
            description: 'Toggle visibility of service prices'
        },
        {
            fieldName: 'serviceBorder',
            label: 'Service Border',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'serviceBorderWidth',
            label: 'Service Border Width',
            type: 'slider',
            defaultValue: 1,
            min: 1,
            max: 5,
            step: 1,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#607d8b',
            description: 'Width of borders around service cards'
        },
        {
            fieldName: 'serviceBorderRadius',
            label: 'Service Border Radius',
            type: 'slider',
            defaultValue: 16,
            min: 0,
            max: 30,
            step: 2,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#E91E63',
            description: 'Border radius of service cards'
        },
        {
            fieldName: 'serviceShadow',
            label: 'Service Shadow',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'serviceHoverEffect',
            label: 'Hover Effects',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'serviceHoverShadow',
            label: 'Hover Shadow',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'borderRadius',
            label: 'Widget Border Radius',
            type: 'number',
            defaultValue: 0,
            min: 0,
            max: 30,
            step: 2,
            unit: 'px',
            icon: '/img/element.svg'
        },
        
        {
            fieldName: 'titleHighlightWeight',
            label: 'Title Highlight Weight',
            type: 'dropdown',
            options: [
                { value: '300', label: 'Light' },
                { value: '400', label: 'Normal' },
                { value: '500', label: 'Medium' },
                { value: '600', label: 'Semi Bold' },
                { value: '700', label: 'Bold' },
                { value: '800', label: 'Extra Bold' },
                { value: '900', label: 'Black' }
            ],
            defaultValue: '800',
            icon: '/img/text-fat.svg'
        },
        // ✅ NEU: Margin Controls für präzise Positionierung
        {
            fieldName: 'titleHighlightMarginLeft',
            label: 'Highlight Margin Left',
            type: 'slider',
            defaultValue: 0,
            min: -50,        // ✅ Erweitert: -50px für Überlappungen
            max: 50,         // ✅ Erweitert: +50px für große Abstände
            step: 1,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'titleHighlightMarginRight',
            label: 'Highlight Margin Right',
            type: 'slider',
            defaultValue: 0,
            min: -50,
            max: 50,
            step: 1,
            unit: 'px',
            icon: '/img/element.svg'
        }
    ];


    return (
        <div>
            <div className="top-widget-edit-container">

                {/* ✅ LAYOUT SELECTOR 2025 */}
                <LayoutSelector2025
                    widgetType="simplicisticServices"
                    selectedLayout={editData.layoutType || 'classic-grid'}
                    onLayoutChange={handleLayoutChange}
                />


                {/* REMOVED: All Grid, Service Card, Icon, Effects Settings moved to Advanced Settings Component */}

                {/* Services Management */}
                <ServicesManagement 
                    services={services}
                    addService={addService}
                    removeService={removeService}
                    updateService={updateService}
                    handleServiceIconUpload={handleServiceIconUpload}
                    handleServiceImageUpload={handleServiceImageUpload}
                    handleIconSelect={handleIconSelect}
                    uploadingStates={uploadingStates}
                    iconPickerStates={iconPickerStates}
                    setIconPickerState={setIconPickerState}
                    setUploadingState={setUploadingState}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    uploadImage={uploadImage}
                    getServiceUploadState={getServiceUploadState}
                    setServiceUploadState={setServiceUploadState}
                />

                {/* Text Settings */}
                <TextSettingsSection 
                    textConfig={textConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    showFontSelector={true}
                    showTextEffects={true}
                    showButtonEffects={false}
                />

                {/* 🚀 REVOLUTIONARY LAYOUT SETTINGS - Standard + Custom Properties */}
                <RevolutionaryLayoutSettings 
                    editData={editData}
                    handleInputChange={handleInputChange}
                    customProperties={customLayoutProperties}
                />

                {/* 🔧 ADVANCED SETTINGS - Nur noch Widget-spezifische Properties */}
                <AdvancedSettingsSection 
                    advancedConfig={advancedConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    handleSliderChange={handleSliderChange}
                    defaultExpanded={false}
                />

                {/* More Settings (Colors & Fonts only) */}
                <MoreSettingsSection 
                    colors={colors}
                    colorConfig={colorConfig}
                    fontConfig={fontConfig}
                    layoutConfig={[]} // Empty - moved to Revolutionary Layout Settings
                    editData={editData}
                    handleInputChange={handleInputChange}
                />

                {/* 💾 SAVE AS TEMPLATE BUTTON */}
                <div className="save-template-section">
                    <button 
                        className="save-template-btn"
                        onClick={() => setShowSaveTemplatePopup(true)}
                        title="Save this widget configuration as a reusable template"
                    >
                        <i className="fas fa-save"></i>
                        Save as Template
                    </button>
                </div>

            {/* Image Too Big Popup */}
            <ImageTooBigPopup 
                isOpen={showImageTooBigPopup}
                onClose={() => setShowImageTooBigPopup(false)}
                fileSize={tooBigFileInfo.size}
                fileName={tooBigFileInfo.name}
                maxSize={2}
            />

            {/* 💾 SAVE TEMPLATE POPUP */}
            <SaveTemplatePopup
                isOpen={showSaveTemplatePopup}
                onClose={() => setShowSaveTemplatePopup(false)}
                widgetData={editData}
                originalWidgetType={widget.type}
            />

            </div>
        </div>
    );
}

export default SimplisticServicesEdit;
